'use client';

import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn.ts';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretches to its container — for a form's submit, or a mobile call to action. */
  block?: boolean;
  /**
   * Shows a spinner and blocks further clicks. The label stays in place rather than being
   * replaced, so the button does not change width and shift the layout around it.
   */
  loading?: boolean;
  /** Announced while loading. Without it a screen reader hears nothing change. */
  loadingLabel?: string;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  className?: string;
}

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  loading = false,
  loadingLabel = 'Loading',
  iconStart,
  iconEnd,
  disabled,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      // Defaulting to "button": an unspecified type inside a form is "submit", which is how a
      // secondary action ends up submitting the form it sits in.
      type={type}
      className={cn(
        'awj-button',
        `awj-button--${variant}`,
        size !== 'md' && `awj-button--${size}`,
        block && 'awj-button--block',
        className,
      )}
      disabled={disabled || loading}
      // Distinct from disabled: this says *why* the control is unavailable.
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <>
          <span className="awj-button__spinner" aria-hidden="true" />
          <span className="awj-visually-hidden">{loadingLabel}</span>
        </>
      ) : (
        iconStart
      )}
      {children}
      {!loading && iconEnd}
    </button>
  );
}
